import { useQueryClient } from "@tanstack/react-query";
import { clsx } from "clsx";
import { FlaskConical, LayoutDashboard, Menu, MessagesSquare, Settings, X } from "lucide-react";
import { m } from "motion/react";
import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router";

import { useIsAdmin } from "../../api/auth";
import { refreshKnowledge, useActiveJobs, useHealth, useSystem } from "../../api/queries";
import { AccountMenu } from "../auth/AccountMenu";
import { pageFade } from "../../lib/motion";
import { AnimatedOutlet } from "../../ui/AnimatedOutlet";
import { GridBackdrop } from "../../ui/backdrop/GridBackdrop";
import { Logo } from "../../ui/Logo";
import { Alert } from "../../ui/Primitives";
import { ThemeSwitcher } from "../../ui/ThemeSwitcher";
import styles from "./AppShell.module.css";
import { StatusDrawer } from "./StatusDrawer";

// An account with the user role sees Chat alone; the rest is the admin's.
const NAV = [
  { to: "/chat", label: "Chat", icon: MessagesSquare, adminOnly: false },
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, adminOnly: true },
  { to: "/settings", label: "Settings", icon: Settings, adminOnly: true },
];

export function AppShell() {
  const admin = useIsAdmin();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPath, setMenuPath] = useState(location.pathname);
  if (menuPath !== location.pathname) {
    setMenuPath(location.pathname);
    setMenuOpen(false);
  }

  return (
    <div className={styles.shell}>
      <a href="#main" className="skip-link">
        Skip to content
      </a>
      <GridBackdrop />

      <header className={styles.mobileBar}>
        <Brand />
        <button
          type="button"
          className={styles.menuButton}
          onClick={() => setMenuOpen((v) => !v)}
          aria-expanded={menuOpen}
          aria-controls="app-sidebar"
          aria-label={menuOpen ? "Close menu" : "Open menu"}
        >
          {menuOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </header>

      <aside id="app-sidebar" className={clsx(styles.sidebar, menuOpen && styles.sidebarOpen)}>
        <div className={styles.sidebarTop}>
          <Brand />
          <span className={styles.credit}>
            by{" "}
            <a href="https://theninehertz.com" target="_blank" rel="noopener noreferrer" className={styles.creditLink}>
              NineHertz
            </a>
          </span>
        </div>
        <nav aria-label="Main" className={styles.nav}>
          {NAV.filter((item) => admin || !item.adminOnly).map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} className={styles.navLink}>
              {({ isActive }) => (
                <>
                  {isActive ? (
                    <m.span
                      layoutId="nav-active"
                      className={styles.navActive}
                      transition={{ type: "spring", stiffness: 500, damping: 40 }}
                    />
                  ) : null}
                  <Icon size={18} aria-hidden="true" className={styles.navIcon} />
                  <span className={styles.navLabel}>{label}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>
        <div className={styles.sidebarBottom}>
          {admin ? <HealthCard /> : null}
          <ThemeSwitcher />
          <AccountMenu />
        </div>
      </aside>
      {menuOpen ? (
        <button
          type="button"
          className={styles.scrim}
          aria-label="Close menu"
          onClick={() => setMenuOpen(false)}
        />
      ) : null}

      <main id="main" className={styles.main} tabIndex={-1} data-page={location.pathname.split("/")[1] || undefined}>
        <VendorRibbon />
        <ConfigurationNotice />
        <AnimatedOutlet variants={pageFade} className={styles.page} />
      </main>
      {admin ? <IngestionAnnouncer /> : null}
    </div>
  );
}

function Brand() {
  return (
    <NavLink to="/chat" className={styles.brand}>
      <Logo className={styles.brandLogo} />
    </NavLink>
  );
}

/** Services at a glance; opens System status in a side panel, also for an address ending in
 * #system (the old links to it in Settings and on the Dashboard). */
function HealthCard() {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const linked = location.hash === "#system";
  const [seenLink, setSeenLink] = useState(false);
  if (linked !== seenLink) {
    setSeenLink(linked);
    if (linked) setOpen(true);
  }
  const close = () => {
    setOpen(false);
    if (linked) navigate({ search: location.search, hash: "" }, { replace: true });
  };
  const health = useHealth();
  const status = health.data?.status ?? (health.isError ? "down" : "unknown");
  const checks = Object.values(health.data?.checks ?? {});
  const ready = checks.filter((check) => check.status === "ok").length;
  const title =
    status === "ok"
      ? "System healthy"
      : status === "degraded"
        ? "Needs attention"
        : status === "down"
          ? "Service down"
          : "Checking…";
  const detail = checks.length ? `${ready} of ${checks.length} services ready` : "Status every 30 s";
  return (
    <>
      <button
        type="button"
        className={styles.health}
        data-status={status}
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
      >
        <span className={styles.healthDot} aria-hidden="true" />
        <span className={styles.healthText}>
          <span className={styles.healthTitle}>{title}</span>
          <span className={styles.healthSub}>{detail}</span>
        </span>
      </button>
      <StatusDrawer open={open} onClose={close} />
    </>
  );
}

function VendorRibbon() {
  const system = useSystem();
  if (system.data?.vendor_mode !== "simulated") return null;
  return (
    <p className={styles.ribbon} role="note">
      <FlaskConical size={15} aria-hidden="true" />
      Simulated vendors: JEV and the language model are local stand-ins, so answers and tests
      are not real calls.
    </p>
  );
}

/** The services are configured on the server (.env). Until they are, the chat cannot answer,
 * so every page says what is missing. */
function ConfigurationNotice() {
  const system = useSystem();
  if (!system.data || system.data.configured) return null;
  return (
    <Alert tone="danger" title="The assistant is not configured yet" className={styles.notice}>
      <ul className={styles.problems}>
        {system.data.problems.map((problem) => (
          <li key={problem}>{problem}</li>
        ))}
      </ul>
      <p>These are set on the server, in its .env file; restart the server after changing them.</p>
    </Alert>
  );
}

/**
 * Announces finished ingestion jobs once to screen readers, and refreshes the views that
 * show knowledge bases when a job ends.
 */
function IngestionAnnouncer() {
  const jobs = useActiveJobs();
  const client = useQueryClient();
  const known = useRef(new Map<string, string>());
  const [message, setMessage] = useState("");

  useEffect(() => {
    const current = new Map((jobs.data ?? []).map((job) => [job.job_id, job.file_name]));
    const finished = [...known.current.entries()].filter(([id]) => !current.has(id));
    if (finished.length) {
      refreshKnowledge(client);
      setMessage(
        finished.length === 1
          ? `${finished[0]?.[1]} finished indexing.`
          : `${finished.length} documents finished indexing.`,
      );
    }
    known.current = current;
  }, [jobs.data, client]);

  return (
    <div className="visually-hidden" aria-live="polite" role="status">
      {message}
    </div>
  );
}
